import styled from 'styled-components';

interface BaseContainerProps {
  $variant: 'success' | 'error';
}

export const BaseContainer = styled.div<BaseContainerProps>`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 32px;
    
    background: linear-gradient(135deg, 
        rgba(255, 255, 255, 0.98) 0%, 
        ${props => props.$variant === 'success' 
            ? 'rgba(248, 250, 252, 0.95)' 
            : 'rgba(254, 242, 242, 0.95)'
        } 100%
    );
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    
    padding: 56px 48px;
    border-radius: 24px;
    box-shadow: 
        0 25px 80px rgba(0, 0, 0, 0.08),
        ${props => props.$variant === 'success' 
            ? '0 12px 40px rgba(34, 197, 94, 0.12)' 
            : '0 12px 40px rgba(239, 68, 68, 0.12)'
        },
        inset 0 1px 0 rgba(255, 255, 255, 0.9);
    
    max-width: 480px;
    min-width: 420px;
    
    position: relative;
    
    &::before {
        content: '';
        position: absolute;
        top: -1px;
        left: -1px;
        right: -1px;
        bottom: -1px;
        background: linear-gradient(135deg, 
            ${props => props.$variant === 'success' 
                ? 'rgba(34, 197, 94, 0.2) 0%, rgba(16, 185, 129, 0.1)' 
                : 'rgba(239, 68, 68, 0.2) 0%, rgba(220, 38, 38, 0.1)'
            } 100%
        );
        border-radius: 24px;
        z-index: -1;
    }
    
    @media (max-width: 520px) {
        padding: 48px 32px;
        min-width: auto;
        max-width: calc(100vw - 40px);
        gap: 24px;
    }
    
    @media (max-width: 440px) {
        padding: 40px 24px;
        max-width: calc(100vw - 32px);
        gap: 20px;
    }
`;

export const BaseTitle = styled.div<BaseContainerProps>`
    font-weight: 700;
    font-size: 28px;
    background: linear-gradient(135deg, 
        ${props => props.$variant === 'success' 
            ? '#22c55e 0%, #16a34a' 
            : '#ef4444 0%, #dc2626'
        } 100%
    );
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-align: center;
    line-height: 1.3;
    letter-spacing: -0.02em;
    
    @media (max-width: 520px) {
        font-size: 24px;
    }
`;

export const BaseIconContainer = styled.div<BaseContainerProps>`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 120px;
    height: 120px;
    border-radius: 50%;
    background: linear-gradient(135deg, 
        ${props => props.$variant === 'success' 
            ? 'rgba(34, 197, 94, 0.1) 0%, rgba(16, 185, 129, 0.05)' 
            : 'rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.05)'
        } 100%
    );
    position: relative;
    
    &::before {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 100px;
        height: 100px;
        border-radius: 50%;
        background: linear-gradient(135deg, 
            ${props => props.$variant === 'success' 
                ? 'rgba(34, 197, 94, 0.15) 0%, rgba(16, 185, 129, 0.08)' 
                : 'rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.08)'
            } 100%
        );
        animation: pulse 2s ease-in-out infinite;
    }
    
    @keyframes pulse {
        0%, 100% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 1;
        }
        50% {
            transform: translate(-50%, -50%) scale(1.1);
            opacity: 0.7;
        }
    }
    
    @media (max-width: 520px) {
        width: 100px;
        height: 100px;
        
        &::before {
            width: 80px;
            height: 80px;
        }
    }
`;

export const BaseButtonContainer = styled.div`
    width: 100%;
    max-width: 320px;
    
    @media (max-width: 520px) {
        max-width: 100%;
    }
`;

export const BaseMessageContainer = styled.div`
    width: 100%;
    text-align: center;
`;

export const BaseMessage = styled.div`
    color: #374151;
    font-size: 16px;
    line-height: 1.6;
    font-weight: 500;
    text-align: center;
    letter-spacing: 0.025em;
    
    &:not(:last-child) {
        margin-bottom: 8px;
    }
    
    @media (max-width: 520px) {
        font-size: 15px;
    }
`;

export const BaseErrorText = styled.div`
    width: 100%;
    max-height: 200px;
    overflow-y: auto;
    overflow-x: hidden;
    text-align: center;
    
    color: #374151;
    font-size: 16px;
    line-height: 1.6;
    font-weight: 500;
    letter-spacing: 0.025em;
    
    white-space: normal;
    word-wrap: break-word; 
    word-break: break-word; 
    text-overflow: ellipsis;
    
    padding: 16px;
    box-sizing: border-box;
    
    /* Estilo da scrollbar */
    &::-webkit-scrollbar {
        width: 6px;
    }
    
    &::-webkit-scrollbar-track {
        background: rgba(239, 68, 68, 0.1);
        border-radius: 3px;
    }
    
    &::-webkit-scrollbar-thumb {
        background: rgba(239, 68, 68, 0.3);
        border-radius: 3px;
    }
    
    &::-webkit-scrollbar-thumb:hover {
        background: rgba(239, 68, 68, 0.5);
    }
    
    @media (max-width: 520px) {
        font-size: 15px;
        padding: 12px;
    }
`;

export const BaseLoadingContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 24px;
    
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.95) 100%);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    
    padding: 48px 56px;
    border-radius: 24px;
    box-shadow: 
        0 25px 80px rgba(0, 0, 0, 0.08),
        0 12px 40px rgba(116, 76, 180, 0.12),
        inset 0 1px 0 rgba(255, 255, 255, 0.9);
    
    max-width: 420px;
    min-width: 360px;
    
    position: relative;
    
    &::before {
        content: '';
        position: absolute;
        top: -1px;
        left: -1px;
        right: -1px;
        bottom: -1px;
        background: linear-gradient(135deg, rgba(116, 76, 180, 0.2) 0%, rgba(99, 102, 241, 0.1) 100%);
        border-radius: 24px;
        z-index: -1;
    }
    
    @media (max-width: 520px) {
        padding: 40px 48px;
        min-width: auto;
        max-width: calc(100vw - 40px);
        gap: 20px;
    }
    
    @media (max-width: 440px) {
        padding: 32px 40px;
        max-width: calc(100vw - 32px);
        gap: 16px;
    }
`;

export const BaseLoadingMessage = styled.span`
    color: #374151;
    font-size: 18px;
    line-height: 1.5;
    font-weight: 600;
    text-align: center;
    letter-spacing: 0.025em;
    
    @media (max-width: 520px) {
        font-size: 16px;
    }
`;
